import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, BlockType, Define, ValidationMessage, ValidationMessageType, TibboDataType,
} from '@appblocks/codegeneration/types';

export default class WifiWM2000 extends BaseFeature {
    constructor() {
        super();
        this.name = 'Wi-Fi WM2000';
        this.id = 'wifiwm2000';
        this.requirements = [['WM2000', 'WM2000EV', 'WS1102']];
        this.priority = 99;
        const settings = [
            {
                id: 'ENABLED',
                name: 'Enabled',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '1',
            },
            {
                id: 'DHCP',
                name: 'DHCP',
                settingType: SettingType.OPTION,
                default: '1',
                required: true,
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'WAP',
                name: 'Access Point SSID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFAP',
                    NAME: 'Wifi AP SSID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                default: '',
            },
            {
                id: 'WPW',
                name: 'Password',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFPW',
                    NAME: 'Wifi Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                },
                UIControl: {
                    CONTROL: 'PASSWORD',
                },
                required: true,
                default: '',
            },
            {
                id: 'IP',
                name: 'IP Address',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFIP',
                    NAME: 'Wifi IP',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
            {
                id: 'NM',
                name: 'Netmask',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFNM',
                    NAME: 'Wifi Netmask',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '255.255.255.0',
            },
            {
                id: 'GI',
                name: 'Gateway',
                settingType: SettingType.IP,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WIFGW',
                    NAME: 'Wifi Gateway',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 16,
                },
                UIControl: {
                    CONTROL: 'IPCTRL',
                },
                required: false,
                default: '1.0.0.1',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [{
            name: 'WLN_AVAILABLE',
            value: '1',
        }];
        return defines;
    }

    getCodeBlocks() {
        const blocks = [];
        const WPW = this.getSettingValue('WPW');
        const WAP = this.getSettingValue('WAP');
        const NETMASK = this.getSettingValue('NM');
        const IP = this.getSettingValue('IP');
        const GATEWAY = this.getSettingValue('GI');
        const DHCP = this.getSettingValue('DHCP') === '1';
        let bootCode = '';
        bootCode += `
    'WM2000 Wi-Fi setup
    '================================================================`;

        if (WAP !== '""' || DHCP) {
            bootCode += `
    dim reboot_required as boolean = false
        `;
        }
        if (WAP !== '""') {
            bootCode += `  
    if wln.autoconnectssid<>${WAP} then 
        wln.autoconnectssid=${WAP}
        reboot_required=true
    end if

    if wln.autoconnectpassword<>${WPW} then 
        wln.autoconnectpassword=${WPW}
        reboot_required=true
    end if
`;
        }


        if (DHCP) {
            bootCode += `   
    if wln.autodhcp<>YES then
        wln.autodhcp=YES
        
        
        reboot_required=true
    end if`;


            blocks.push({
                location: 'on_sys_dhcp_ok',
                blockType: BlockType.CODE,
                template: `
        if renew=YES and wln.ip<>ip then
            'this is a lease renewal and the DHCP server has issues new IP
            'it is better to reboot than deal with the implications of the changed IP
            sys.reboot
        end if

        #if DEV_DEBUG_PRINT
            dev_debugprint("WLN DHCP OK ("+ip+")")
        #endif

        if wln.ip<>ip then
            sock.inconenabledmaster=NO
            close_interface_sockets(PL_SOCK_INTERFACE_WLN)
            wln.ip=ip
            if gateway_ip<>"" then wln.gatewayip=gateway_ip
            if netmask<>"" then wln.netmask=netmask
            sock.inconenabledmaster=YES
        end if
        interface_set(PL_SOCK_INTERFACE_WLN,YES)
        '****${this.project.getNetworkConnectedLocation('PL_SOCK_INTERFACE_WLN')}****'
            `,
            });
        } else {
            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                template: `
declare device_wln_ip as string
declare device_wln_mask as string
declare device_wln_gateway as string`,
            });

            blocks.push({
                location: 'GLOBAL_DIMS',
                blockType: BlockType.CODE,
                template: `
dim device_wln_ip as string
dim device_wln_mask as string
dim device_wln_gateway as string`,
            });

            bootCode += `
    device_wln_ip = ${IP}
    device_wln_mask = ${NETMASK}
    device_wln_gateway  = ${GATEWAY}
    wln.ip=device_wln_ip
    wln.netmask=device_wln_mask
    wln.gatewayip=device_wln_gateway`;
        }

        if (WAP !== '""' || DHCP) {
            bootCode += `
    if reboot_required = true then
        sys.reboot
    end if
    `;
        }

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: bootCode,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_net';
    }
}
